/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * useInvestorNotifications — React hook for investor notification state
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Wraps InvestorNotificationService in a React-friendly interface with
 * memoised output, refresh capability, and derived counts.
 *
 * @module hooks/useInvestorNotifications
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    type InvestorNotification,
    type InvestorNotificationSeverity,
    type InvestorNotificationType,
} from '@/types/investorNotifications';
import type { CanonicalInvestorEntityReference } from '@/types/investorEntityReference';
import type { InvestorEntityType } from '@/lib/investor/routing';
import { getInvestorNotificationService } from '@/services/InvestorNotificationService';
import {
    useNotifications,
    type Notification as CanonicalNotification,
} from '@/features/notifications/hooks/useNotifications';
import { isDemoMode } from '@/lib/supabase';

const INVESTOR_NOTIFICATION_TYPES = new Set<InvestorNotificationType>([
    'milestone_verified',
    'distribution_paid',
    'distribution_pending',
    'evidence_available',
    'release_completed',
    'portfolio_update',
    'alert',
    'approval_requested',
    'approval_decided',
    'signature_requested',
    'signature_completed',
    'finance_approval_requested',
    'finance_approval_decided',
    'dataroom_access_granted',
]);

export function isInvestorNotificationType(type: string): type is InvestorNotificationType {
    return INVESTOR_NOTIFICATION_TYPES.has(type as InvestorNotificationType);
}

const INVESTOR_ENTITY_TYPES = new Set<InvestorEntityType>([
    'milestone',
    'distribution',
    'evidence_pack',
    'document',
    'approval_workflow',
    'signature_request',
    'gate',
    'finance_approval',
    'dataroom',
    'project',
]);

function optionalMetadataString(
    metadata: Record<string, unknown>,
    ...keys: string[]
): string | undefined {
    for (const key of keys) {
        const value = metadata[key];
        if (typeof value === 'string' && value.trim()) return value;
    }
    return undefined;
}

function notificationSeverity(
    notification: CanonicalNotification,
    type: InvestorNotificationType,
): InvestorNotificationSeverity {
    const supplied = optionalMetadataString(notification.metadata, 'severity');
    if (supplied === 'info' || supplied === 'success' || supplied === 'warning' || supplied === 'critical') {
        return supplied;
    }
    if (type === 'alert') return 'warning';
    if (type.endsWith('_decided')
        || type.endsWith('_completed')
        || type === 'distribution_paid'
        || type === 'milestone_verified') {
        return 'success';
    }
    return 'info';
}

function notificationEntityType(
    notification: CanonicalNotification,
    type: InvestorNotificationType,
): InvestorEntityType | undefined {
    if (notification.entity_type && INVESTOR_ENTITY_TYPES.has(notification.entity_type as InvestorEntityType)) {
        return notification.entity_type as InvestorEntityType;
    }
    if (type.startsWith('finance_approval_')) return 'finance_approval';
    if (type.startsWith('approval_')) return 'approval_workflow';
    if (type.startsWith('signature_')) return 'signature_request';
    if (type.startsWith('distribution_') || type === 'release_completed') return 'distribution';
    if (type === 'milestone_verified') return 'milestone';
    if (type === 'evidence_available') return 'evidence_pack';
    if (type === 'dataroom_access_granted') return 'dataroom';
    return undefined;
}

export function mapCanonicalNotificationToInvestor(
    notification: CanonicalNotification,
): InvestorNotification {
    const type = INVESTOR_NOTIFICATION_TYPES.has(notification.type as InvestorNotificationType)
        ? notification.type as InvestorNotificationType
        : 'alert';
    const referenceId = notification.entity_id
        ?? optionalMetadataString(notification.metadata, 'referenceId', 'reference_id');
    const entityType = notificationEntityType(notification, type);
    const projectId = optionalMetadataString(notification.metadata, 'projectId', 'project_id');
    const entityRef: CanonicalInvestorEntityReference | undefined = referenceId && entityType
        ? {
            entityId: referenceId,
            entityType,
            workspaceId: optionalMetadataString(notification.metadata, 'workspaceId', 'workspace_id'),
            organizationId: optionalMetadataString(notification.metadata, 'organizationId', 'organization_id'),
            investmentId: optionalMetadataString(notification.metadata, 'investmentId', 'investment_id'),
            projectId,
            sourceSystem: optionalMetadataString(notification.metadata, 'sourceSystem', 'source_system')
                ?? 'ax1-notifications',
            asOf: notification.created_at,
            currency: optionalMetadataString(notification.metadata, 'currency'),
            provenance: {
                authority: 'notifications',
                notificationId: notification.id,
                sourceSecurityEventId: notification.source_security_event_id,
            },
        }
        : undefined;

    return {
        id: notification.id,
        investorId: notification.user_id,
        type,
        title: notification.title,
        message: notification.body,
        projectId,
        projectName: optionalMetadataString(notification.metadata, 'projectName', 'project_name'),
        referenceId,
        entityRef,
        createdAt: notification.created_at,
        read: notification.read_at !== null,
        severity: notificationSeverity(notification, type),
    };
}

export interface UseInvestorNotificationsResult {
    /** All notifications for this investor, newest first. */
    notifications: InvestorNotification[];
    /** Count of unread notifications. */
    unreadCount: number;
    /** Whether persisted notification delivery records are loading. */
    loading: boolean;
    /** Production read failure; Demo remains an explicit local showcase. */
    error: string | null;
    /** Mark a single notification as read. */
    markAsRead: (notificationId: string) => void;
    /** Mark all notifications as read. */
    markAllAsRead: () => void;
    /** Re-fetch notifications from the service. */
    refresh: () => void;
}

export function useInvestorNotifications(
    investorId: string | undefined,
): UseInvestorNotificationsResult {
    const [tick, setTick] = useState(0);
    const service = useMemo(() => getInvestorNotificationService(), []);
    const {
        notifications: persistedNotifications,
        loading: persistedLoading,
        error: persistedError,
        markAsRead: markPersistedAsRead,
        markManyAsRead: markPersistedManyAsRead,
        refresh: refreshPersistedNotifications,
    } = useNotifications(50);

    const showcaseNotifications = useMemo<InvestorNotification[]>(() => {
        if (!investorId) return [];
        return service.getNotifications(investorId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [investorId, service, tick]);

    const showcaseUnreadCount = useMemo(() => {
        if (!investorId) return 0;
        return service.getUnreadCount(investorId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [investorId, service, tick]);

    const refresh = useCallback(() => {
        if (isDemoMode) {
            setTick((t) => t + 1);
            return;
        }
        void refreshPersistedNotifications();
    }, [refreshPersistedNotifications]);

    useEffect(() => {
        if (!isDemoMode) return undefined;
        const unsubscribe = service.subscribe(() => {
            setTick((t) => t + 1);
        });
        return unsubscribe;
    }, [service]);

    const markAsRead = useCallback(
        (notificationId: string) => {
            if (isDemoMode) {
                service.markAsRead(notificationId);
                setTick((t) => t + 1);
                return;
            }
            void markPersistedAsRead(notificationId);
        },
        [markPersistedAsRead, service],
    );

    const markAllAsRead = useCallback(() => {
        if (!investorId) return;
        if (isDemoMode) {
            service.markAllAsRead(investorId);
            setTick((t) => t + 1);
            return;
        }
        const unreadInvestorIds = persistedNotifications
            .filter((notification) => (
                notification.user_id === investorId
                && notification.read_at === null
                && isInvestorNotificationType(notification.type)
            ))
            .map((notification) => notification.id);
        void markPersistedManyAsRead(unreadInvestorIds);
    }, [investorId, markPersistedManyAsRead, persistedNotifications, service]);

    const canonicalNotifications = useMemo(
        () => investorId
            ? persistedNotifications
                .filter((notification) => (
                    notification.user_id === investorId
                    && isInvestorNotificationType(notification.type)
                ))
                .map(mapCanonicalNotificationToInvestor)
            : [],
        [investorId, persistedNotifications],
    );
    const canonicalUnreadCount = useMemo(
        () => canonicalNotifications.filter((notification) => !notification.read).length,
        [canonicalNotifications],
    );

    return {
        notifications: isDemoMode ? showcaseNotifications : canonicalNotifications,
        unreadCount: isDemoMode ? showcaseUnreadCount : canonicalUnreadCount,
        loading: isDemoMode ? false : persistedLoading,
        error: isDemoMode ? null : persistedError,
        markAsRead,
        markAllAsRead,
        refresh,
    };
}
